// ConsultantsPanel.jsx - Hire negotiators, lawyers, fixers

import React, { useState } from 'react';
import { debug } from '../../lib/debug.js';

export function ConsultantsPanel({ consultantState, cash, tier, onHire, onFire, onClose }) {
  const hired = consultantState?.hired || {};
  const available = consultantState?.available || [];

  debug('[ConsultantsPanel] Props:', { cash, tier, hiredCount: Object.keys(hired).length, availableCount: available.length });

  return (
    <div style={styles.overlay}>
      <div style={styles.panel}>
        
        <div style={styles.header}>
          <div style={styles.headerTitle}>CONSULTANTS</div>
          <button style={styles.closeButton} onClick={onClose}>X</button>
        </div>

        {/* On Retainer */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>ON RETAINER</div>
          {Object.keys(hired).length === 0 ? (
            <div style={styles.emptyState}>No consultants hired</div>
          ) : (
            <div style={styles.grid}>
              {Object.entries(hired).map(([type, c]) => (
                <div key={type} style={styles.hiredCard}>
                  <div style={styles.cardTop}>
                    <div style={styles.name}>{c.name}</div>
                    <div style={styles.title}>{c.title}</div>
                  </div>
                  <div style={styles.effects}>{formatEffects(c.effects)}</div>
                  <div style={styles.cardBottom}>
                    <span style={styles.cost}>${formatMoney(c.monthlyCost)}/mo</span>
                    <button style={styles.fireBtn} onClick={() => onFire(type)}>Release</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Monthly Summary */}
        {consultantState?.totalMonthlyCost > 0 && (
          <div style={styles.summary}>
            Total retainer: ${formatMoney(consultantState.totalMonthlyCost)}/month
          </div>
        )}

        {/* Available */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>AVAILABLE FOR HIRE</div>
          <div style={styles.grid}>
            {available
              .filter(c => !hired[c.type])
              .map(c => {
                const canAfford = cash >= c.hireCost;
                const meetsTier = tier >= (c.requirements?.minTier || 1);
                const locked = !canAfford || !meetsTier;
                
                return (
                  <div key={c.id} style={{...styles.availCard, ...(locked ? styles.locked : {})}}>
                    <div style={styles.cardTop}>
                      <div style={styles.name}>{c.name}</div>
                      <div style={styles.title}>{c.title}</div>
                      <div style={styles.specialty}>{c.specialty}</div>
                    </div>
                    <div style={styles.rep}>REP {c.reputation}</div>
                    <div style={styles.desc}>{c.description}</div>
                    <div style={styles.effects}>{formatEffects(c.effects)}</div>
                    <div style={styles.cardBottom}>
                      <div>
                        <div style={styles.cost}>Hire: ${formatMoney(c.hireCost)}</div>
                        <div style={styles.costSmall}>+ ${formatMoney(c.monthlyCost)}/mo</div>
                      </div>
                      <button
                        style={{...styles.hireBtn, ...(locked ? styles.btnLocked : {})}}
                        onClick={() => {
                          debug('[ConsultantsPanel] Hire clicked:', { id: c.id, canAfford, meetsTier, locked });
                          if (!locked) onHire(c.id);
                        }}
                        disabled={locked}
                      >
                        {!canAfford ? 'No Funds' : !meetsTier ? `Tier ${c.requirements.minTier}+` : 'Hire'}
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}

function formatEffects(effects) {
  if (!effects) return null;
  const items = [];
  if (effects.negotiationBonus) items.push(`+${effects.negotiationBonus} Negotiation`);
  if (effects.relationGainMultiplier) items.push(`+${Math.round((effects.relationGainMultiplier - 1) * 100)}% Relations Gain`);
  if (effects.violationPenaltyReduction) items.push(`-${Math.round(effects.violationPenaltyReduction * 100)}% Violation Penalties`);
  if (effects.disputeWinChance) items.push(`${Math.round(effects.disputeWinChance * 100)}% Dispute Win`);
  if (effects.relationRepairRate) items.push(`${effects.relationRepairRate}x Relations Repair`);
  if (effects.backChannelAccess) items.push('Back Channel Access');
  if (effects.intelGathering) items.push('Competitor Intel');
  if (effects.demandVisibility) items.push('Demand Forecasting');
  if (effects.slotAllocationBonus) items.push(`+${effects.slotAllocationBonus} Slot Allocation`);
  return items.map((item, i) => <div key={i} style={styles.effectItem}>{item}</div>);
}

function formatMoney(cents) {
  if (!cents) return '0';
  if (cents >= 100000000) return `${(cents / 100000000).toFixed(1)}M`;
  if (cents >= 100000) return `${(cents / 100000).toFixed(0)}K`;
  return (cents / 100).toLocaleString();
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    width: '800px', maxHeight: '90vh', overflow: 'auto',
    backgroundColor: '#0a0a0a', border: '1px solid #222', borderRadius: '8px',
    fontFamily: 'inherit',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: '1px solid #222',
  },
  headerTitle: {
    fontSize: '13px', fontWeight: '600', letterSpacing: '2px', color: '#fff',
  },
  closeButton: {
    background: 'none', border: 'none', color: '#666', fontSize: '24px', cursor: 'pointer',
  },
  section: { padding: '20px' },
  sectionTitle: {
    fontSize: '10px', letterSpacing: '2px', color: '#666', marginBottom: '12px',
  },
  emptyState: { color: '#444', fontSize: '12px', fontStyle: 'italic' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  summary: {
    padding: '12px 20px', backgroundColor: '#111', borderTop: '1px solid #1a1a1a',
    fontSize: '11px', color: '#888',
  },
  hiredCard: {
    backgroundColor: '#111', border: '1px solid #333', borderRadius: '6px', padding: '14px',
  },
  availCard: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '6px', padding: '14px',
    transition: 'border-color 0.15s',
  },
  locked: { opacity: 0.5 },
  cardTop: { marginBottom: '10px' },
  name: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  title: { fontSize: '11px', color: '#00ff88', marginTop: '2px' },
  specialty: { fontSize: '10px', color: '#666', marginTop: '4px' },
  rep: {
    fontSize: '10px', color: '#888', letterSpacing: '1px', marginBottom: '8px',
  },
  desc: { fontSize: '10px', color: '#555', lineHeight: '1.4', marginBottom: '10px' },
  effects: { marginBottom: '12px' },
  effectItem: {
    fontSize: '10px', color: '#00ff88', padding: '2px 0',
  },
  cardBottom: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
    borderTop: '1px solid #222', paddingTop: '10px', marginTop: '10px',
  },
  cost: { fontSize: '12px', color: '#fff' },
  costSmall: { fontSize: '10px', color: '#666' },
  hireBtn: {
    background: 'none', border: '1px solid #00ff88', borderRadius: '3px',
    padding: '6px 14px', fontSize: '10px', color: '#00ff88', cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnLocked: { borderColor: '#333', color: '#666', cursor: 'not-allowed' },
  fireBtn: {
    background: 'none', border: '1px solid #661111', borderRadius: '3px',
    padding: '4px 10px', fontSize: '9px', color: '#ff4444', cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default ConsultantsPanel;
